import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Inject, Optional, ChangeDetectorRef, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DynamicTemplateComponent } from '../dynamic-template/dynamic-template.component';
import { Subject, Observable } from 'rxjs';
import { takeUntil, take, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TabService, TabState } from '../../services/tab/tab.service';
import { Actions, ofType } from '@ngrx/effects';
import * as TemplateActions from '../../ngrx/template/template.actions';
import * as fromTemplate from '../../ngrx/template/template.selectors';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-dynamic-tabs-content',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    DynamicTemplateComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dynamic-tabs-content.component.html',
  styleUrls: ['./dynamic-tabs-content.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('150ms', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class DynamicTabsContentComponent implements OnInit, OnDestroy {
  slug: string;
  tabState$: Observable<TabState>;
  isActive: boolean;
  isAutoHeight: boolean;
  
  // Apply CSS class when auto-height is enabled
  @HostBinding('class.auto-height-enabled') get autoHeightEnabled() {
    return this.isAutoHeight;
  }
  
  // Static flag to track which tabs have been initialized
  private static initializedTabs = new Set<string>();
  private static loadedTabs = new Set<string>(); // Track which tabs have completed loading
  private destroy$ = new Subject<void>();

  // Private flag to track if this component has loaded data
  private hasLoadedData = false;
  
  // Debugging log helper
  private logDebug(message: string, data?: any): void {
    // Debug logging disabled
    /*
    const prefix = '[TAB-CONTENT-DEBUG]';
    if (data) {
    } else {
    }
    */
  }

  constructor(
    @Inject('TAB_SLUG') slug: string,
    @Optional() @Inject('IS_ACTIVE_TAB') isActive: boolean = false,
    @Optional() @Inject('AUTO_HEIGHT_ENABLED') isAutoHeight: boolean = false,
    private tabService: TabService,
    private cdr: ChangeDetectorRef,
    private store: Store<any>,
    private actions$: Actions
  ) {
    this.slug = slug;
    this.isActive = isActive;
    this.isAutoHeight = isAutoHeight;
    this.tabState$ = this.tabService.getTabState$(slug);
    
    this.logDebug(`Created tab, active: ${this.isActive}, autoHeight: ${this.isAutoHeight}`);
    
    // Check if this tab was already loaded before
    if (DynamicTabsContentComponent.loadedTabs.has(this.slug)) {
      // If it was loaded before, check store to verify data is still there
      this.store.select(fromTemplate.selectSlugData(this.slug))
        .pipe(take(1))
        .subscribe(data => {
          if (data && data.loaded && data.customSlug) {
            // Data exists, mark as loaded
            this.logDebug(`Tab was previously loaded, restoring state`);
            this.tabService.updateTabState(this.slug, { loading: false, failed: false, loaded: true });
            this.hasLoadedData = true;
            
            // Force immediate update to prevent flashing
            Promise.resolve().then(() => {
              this.cdr.detectChanges();
              this.logDebug(`Change detection applied after restoration`);
            });
          } else {
            // Data not in store, need to load
            this.logDebug(`Tab was previously loaded but data missing, reloading`);
            DynamicTabsContentComponent.loadedTabs.delete(this.slug);
            this.tabService.updateTabState(this.slug, { loading: true, failed: false, loaded: false });
          }
        });
    } else {
      // New tab, check store first before marking as loaded
      this.store.select(fromTemplate.selectSlugData(this.slug))
        .pipe(take(1))
        .subscribe(data => {
          if (data && data.loaded && data.customSlug) {
            // Data already in store, mark as loaded
            this.logDebug(`Data already in store, marking as loaded`);
            this.tabService.updateTabState(this.slug, { loading: false, failed: false, loaded: true });
            DynamicTabsContentComponent.loadedTabs.add(this.slug);
            this.hasLoadedData = true;
          } else {
            // Data not in store, mark as loading
            this.logDebug(`Data not in store, marking as loading`);
            this.tabService.updateTabState(this.slug, { loading: true, failed: false, loaded: false });
          }
          
          // Force immediate update
          Promise.resolve().then(() => {
            this.cdr.detectChanges();
            this.logDebug(`Initial change detection cycle triggered`);
          });
        });
    }
  }

  ngOnInit(): void {
    this.logDebug(`ngOnInit called, isActive: ${this.isActive}`);
    
    // Track initialization status for this slug
    const isFirstInit = !DynamicTabsContentComponent.initializedTabs.has(this.slug);
    if (isFirstInit) {
      DynamicTabsContentComponent.initializedTabs.add(this.slug);
      this.logDebug(`First initialization for this tab`);
    } else {
      this.logDebug(`Reusing existing tab`);
    }
    
    // Don't reload data if we've already loaded it
    if (this.hasLoadedData) {
      this.logDebug(`Tab has already loaded data, skipping load`);
      return;
    }
    
    // IMPROVED: Always ensure content is loaded before allowing rendering
    this.ensureContentLoaded();
  }

  // New method to ensure content is always loaded
  private ensureContentLoaded(): void {
    // DIRECT APPROACH: Always check store first for existing data
    this.logDebug(`Checking store for existing data`);
    this.store.select(fromTemplate.selectSlugData(this.slug))
      .pipe(take(1))
      .subscribe(data => {
        this.logDebug(`Store data check result:`, data);
        
        if (data && data.loaded && data.customSlug) {
          this.logDebug(`Complete data already loaded in store, marking as loaded`);
          this.tabService.updateTabState(this.slug, { loading: false, failed: false, loaded: true });
          
          // Track that this tab has been loaded
          DynamicTabsContentComponent.loadedTabs.add(this.slug);
          this.hasLoadedData = true;
          
          this.cdr.detectChanges(); // Force immediate update
          this.logDebug(`Change detection applied after marking as loaded`);
        } else {
          // Data not in store or incomplete, mark as loading and dispatch load action
          this.logDebug(`Data not in store or incomplete, dispatching load action`);
          this.tabService.updateTabState(this.slug, { loading: true, failed: false, loaded: false });
          
          // Directly dispatch load action
          this.store.dispatch(TemplateActions.loadPageDataBySlug({ slug: this.slug }));
          
          this.cdr.detectChanges();
          this.logDebug(`Change detection applied after dispatching load action`);
          
          // Poll store to check when data is actually available
          let pollCount = 0;
          const maxPolls = 100; // 10 seconds max (100 * 100ms)
          
          const checkInterval = setInterval(() => {
            pollCount++;
            
            this.store.select(fromTemplate.selectSlugData(this.slug))
              .pipe(take(1))
              .subscribe(storeData => {
                if (storeData && storeData.loaded && storeData.customSlug) {
                  // Data is now available
                  clearInterval(checkInterval);
                  this.tabService.updateTabState(this.slug, { loading: false, failed: false, loaded: true });
                  DynamicTabsContentComponent.loadedTabs.add(this.slug);
                  this.hasLoadedData = true;
                  this.cdr.detectChanges();
                  this.logDebug(`Data loaded successfully after polling`);
                } else if (pollCount >= maxPolls) {
                  // Timeout reached
                  clearInterval(checkInterval);
                  // Even if data isn't complete, mark as loaded to prevent infinite loading
                  this.tabService.updateTabState(this.slug, { loading: false, failed: false, loaded: true });
                  this.cdr.detectChanges();
                  this.logDebug(`Safety timeout: marking as loaded to prevent infinite loading`);
                }
              });
          }, 100); // Check every 100ms
        }
      });
    
    // Listen for load completion actions
    this.actions$.pipe(
      ofType(
        TemplateActions.loadPageDataSuccess,
        TemplateActions.loadPageDataUnchanged
      ),
      filter(action => action.slug === this.slug),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.logDebug(`Data loaded from store action, updating state`);
      this.tabService.updateTabState(this.slug, { loading: false, failed: false, loaded: true });
      
      // Track that this tab has been loaded
      DynamicTabsContentComponent.loadedTabs.add(this.slug);
      this.hasLoadedData = true;
      
      this.cdr.detectChanges(); // Force immediate update
      this.logDebug(`Change detection applied after data load success`);
    });
    
    // Listen for load failures
    this.actions$.pipe(
      ofType(TemplateActions.loadPageDataFailure),
      filter(action => action.error?.slug === this.slug),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.logDebug(`Data load failed`);
      this.tabService.updateTabState(this.slug, { loading: false, failed: true, loaded: true });
      this.cdr.detectChanges(); // Force immediate update
      this.logDebug(`Change detection applied after data load failure`);
    });
    
    // Monitor state changes to keep UI fresh
    this.tabState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      this.logDebug(`Tab state changed:`, state);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.logDebug(`Component destroyed`);
    
    // DO NOT remove from initializedTabs or loadedTabs to preserve state
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void {
    this.logDebug(`Manual reload triggered`);
    // Mark as loading first
    this.tabService.updateTabState(this.slug, { loading: true, failed: false });
    
    // Direct store dispatch for immediate action
    this.store.dispatch(TemplateActions.loadPageDataBySlug({ slug: this.slug }));
    this.cdr.markForCheck();
    this.logDebug(`Store action dispatched for reload`);
  }
}